/**
 * C02. Battle Pass 季節軌道 — 學習端 only (Prodigy Treasure Track移植).
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md §C02.
 * Pillar: Retention (D30 留存 2-3× per Prodigy benchmark).
 *
 * Responsibility split:
 *   - `season-pass-tiers.json` — S1 catalogue (7 tiers × cumulative xpRequired
 *                                 × reward). Append-only after ship.
 *   - `season-pass.ts` (this)  — pure state machine + tick + claim. NO DOM.
 *   - `season-pass-ui.ts`      — home tier-track widget + claim button + reset
 *                                 modal.
 *
 * 🔥 學習與遊戲分離 (CLAUDE.md 紅線):
 *   This module touches PlayerProgress only. It does NOT import anything from
 *   `src/game/`. Cosmetic / mascot / boss rewards are surfaced as flag strings
 *   pushed onto `unlockedSkills` (using the established `cosmetic:` /
 *   `mascot:` / `boss:` namespaces) so the game side can opt-in to detect
 *   ownership without seasonXP altering combat balance.
 *
 *   seasonXP is a *sidechannel* — fed by retention events (streak milestone /
 *   badge unlock / day-one claim / boss defeat) via `addSeasonXP`. It is NOT
 *   wired into faction-XP grants because skills earned via XP must stay
 *   earned via XP.
 *
 * Time model (Phase 1):
 *   We use a *logical day* count derived from `seasonStartedAt`, mirroring
 *   the C05 dayone-hook approach. No setInterval / no real timer — the
 *   season is "over" the next time the player opens home AFTER N calendar
 *   days have elapsed. This keeps the model purely testable (pass `nowMs`)
 *   and resilient to the kid skipping days.
 */

import type { PlayerProgress } from '../../shared/types';
import { addFreeXP, saveProgress, DEFAULT_SEASON_LENGTH_DAYS } from '../../shared/progress';
import tiersData from './season-pass-tiers.json';

// ── Tier catalogue ──────────────────────────────────────────────────────────

/** Reward kinds — discriminated union. New kinds added here + claim() switch. */
export type SeasonReward =
  | { kind: 'freeXP'; amount: number }
  | { kind: 'cosmetic'; id: string }
  | { kind: 'mascot'; id: string }
  | { kind: 'boss'; id: string };

export interface SeasonTier {
  /** 1-indexed tier number. Stable; never renumber after ship. */
  tier: number;
  /** Cumulative seasonXP required to unlock this tier (NOT delta). */
  xpRequired: number;
  /** Short flavor name shown in the tier track. */
  name: string;
  reward: SeasonReward;
  /** Display label for the UI (跟 reward 對應的中文描述). */
  rewardLabel: string;
}

interface TiersJson {
  _doc?: string;
  season: number;
  name: string;
  subtitle: string;
  lengthDays: number;
  xpHints: {
    streakMilestoneXp: number;
    badgeUnlockXp: number;
    dayOneClaimXp: number;
    bossDefeatXp: number;
    perfectExamXp: number;
  };
  tiers: SeasonTier[];
}

const RAW = tiersData as unknown as TiersJson;

/** Frozen Season 1 catalogue — never mutate at runtime. */
export const SEASON_DEF: Readonly<TiersJson> = Object.freeze(RAW);
export const SEASON_TIERS: readonly SeasonTier[] = Object.freeze(RAW.tiers);

/** Canonical XP awards for retention events. Source of truth = JSON. */
export const SEASON_XP_HINTS = Object.freeze({ ...RAW.xpHints });

// ── Source-tag enumeration (for telemetry / debug logs) ─────────────────────

/**
 * Event sources allowed to feed seasonXP. Keeping this enumerated keeps the
 * grep'able audit trail honest — if you add a new source you MUST extend
 * this list AND wire the call site explicitly (no silent grants).
 */
export type SeasonXpSource =
  | 'streak-milestone'
  | 'badge-unlock'
  | 'dayone-claim'
  | 'boss-defeat'
  | 'perfect-exam'
  | 'manual-test';      // tests-only escape hatch

// ── State helpers ───────────────────────────────────────────────────────────

/** Lazy-init the seasonPass sub-object. Mutates p; returns non-optional shape. */
function getSeasonPass(p: PlayerProgress): NonNullable<PlayerProgress['seasonPass']> {
  if (!p.seasonPass) {
    p.seasonPass = {
      currentSeason: 1,
      seasonStartedAt: 0,
      xpThisSeason: 0,
      claimedTiers: [],
      longestSeasonXp: 0,
      seasonLengthDays: DEFAULT_SEASON_LENGTH_DAYS,
    };
  }
  return p.seasonPass;
}

/**
 * Number of full UTC calendar days elapsed between `startMs` and `nowMs`.
 * Day-1 of the season corresponds to elapsed === 0 (the day it started).
 * Returns 0 if the season hasn't been seeded yet (startMs === 0).
 *
 * Mirrors the dayone-hook elapsedDays helper — kept as a private local
 * copy rather than imported to avoid cross-module coupling on a private
 * helper.
 */
function elapsedDays(startMs: number, nowMs: number): number {
  if (startMs === 0) return 0;
  const start = new Date(startMs);
  const now = new Date(nowMs);
  const startKey = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const nowKey = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.max(0, Math.floor((nowKey - startKey) / (24 * 60 * 60 * 1000)));
}

/**
 * Seed `seasonStartedAt` the first time the player lands on home after the
 * v22 migration (or, for fresh accounts, the first ever home render).
 * Idempotent. Returns true on the first seeding, false thereafter.
 */
export function seedSeasonIfNeeded(p: PlayerProgress, nowMs: number = Date.now()): boolean {
  const sp = getSeasonPass(p);
  if (sp.seasonStartedAt > 0) return false;
  sp.seasonStartedAt = nowMs;
  return true;
}

/** True iff the season's calendar window has fully elapsed. */
export function isSeasonExpired(p: PlayerProgress, nowMs: number = Date.now()): boolean {
  const sp = getSeasonPass(p);
  if (sp.seasonStartedAt === 0) return false;
  return elapsedDays(sp.seasonStartedAt, nowMs) >= sp.seasonLengthDays;
}

/**
 * Current logical day within the season (1-indexed; 1 on launch day).
 * Returns 0 if season unseeded. Caps at `seasonLengthDays` so it never
 * over-reports during the brief window between expiry and the next home
 * tick (which calls `tickSeason` to actually reset).
 */
export function currentSeasonDay(p: PlayerProgress, nowMs: number = Date.now()): number {
  const sp = getSeasonPass(p);
  if (sp.seasonStartedAt === 0) return 0;
  return Math.min(sp.seasonLengthDays, 1 + elapsedDays(sp.seasonStartedAt, nowMs));
}

// ── XP accumulation ─────────────────────────────────────────────────────────

/**
 * Grant seasonXP to the current season.
 *
 * If the season is expired we DO NOT silently roll the grant into the next
 * season — the caller's mental model is "this XP belongs to NOW", so we
 * auto-reset first via `tickSeason(p, nowMs)`, THEN credit. This matches
 * Prodigy's behavior: a brand-new season's first event lands in the new
 * season's bucket.
 *
 * Returns the new `xpThisSeason`. Caller is responsible for `saveProgress`
 * batching when multiple grants happen in one tick.
 */
export function addSeasonXP(
  p: PlayerProgress,
  amount: number,
  _source: SeasonXpSource = 'manual-test',
  nowMs: number = Date.now(),
): number {
  // Source tag is currently advisory (telemetry hook) — keep the param so
  // callers grep'able, prefixed underscore silences noUnusedParameters.
  void _source;
  if (!Number.isFinite(amount) || amount <= 0) return getSeasonPass(p).xpThisSeason;
  seedSeasonIfNeeded(p, nowMs);
  // Auto-rollover BEFORE crediting — keeps grants on the right side of the boundary.
  tickSeason(p, nowMs);
  const sp = getSeasonPass(p);
  sp.xpThisSeason += amount;
  if (sp.xpThisSeason > sp.longestSeasonXp) sp.longestSeasonXp = sp.xpThisSeason;
  return sp.xpThisSeason;
}

// ── Tier progression / claim ────────────────────────────────────────────────

/** Highest tier whose `xpRequired` is <= `xp`. 0 if below tier 1. */
export function highestUnlockedTier(xp: number): number {
  let best = 0;
  for (const t of SEASON_TIERS) {
    if (xp >= t.xpRequired) best = t.tier;
    else break; // tiers are in ascending xpRequired order
  }
  return best;
}

/** Next un-reached tier given current xp; null if all done. */
export function nextTier(xp: number): SeasonTier | null {
  for (const t of SEASON_TIERS) {
    if (xp < t.xpRequired) return t;
  }
  return null;
}

/** Tiers currently unlocked-but-not-yet-claimed. Pure read. */
export function pendingClaims(p: PlayerProgress): SeasonTier[] {
  const sp = getSeasonPass(p);
  const claimed = new Set(sp.claimedTiers);
  const unlocked = highestUnlockedTier(sp.xpThisSeason);
  const out: SeasonTier[] = [];
  for (const t of SEASON_TIERS) {
    if (t.tier <= unlocked && !claimed.has(t.tier)) out.push(t);
  }
  return out;
}

/**
 * Claim a single tier. Idempotent — second call returns null. Returns the
 * tier def on success.
 *
 * Side effects on success:
 *   1. Push tier number to `claimedTiers`
 *   2. Apply reward:
 *        - freeXP   → addFreeXP (also bumps dailyXP)
 *        - cosmetic → push `cosmetic:<id>` onto unlockedSkills (dedup)
 *        - mascot   → push `mascot:<id>` onto unlockedSkills (dedup)
 *        - boss     → push `boss:<id>`   onto unlockedSkills (dedup)
 *      (We piggyback on unlockedSkills because that array is already the
 *      cross-feature "flag bag" — see dayone-hook tutorial-boss-trophy
 *      and badges category 'collect' precedent.)
 *   3. saveProgress(p)
 *
 * Returns null when:
 *   - `tier` is out of range
 *   - tier not yet unlocked (xpThisSeason < tier.xpRequired)
 *   - already in claimedTiers
 */
export function claimTier(p: PlayerProgress, tier: number): SeasonTier | null {
  const def = SEASON_TIERS.find((t) => t.tier === tier);
  if (!def) return null;
  const sp = getSeasonPass(p);
  if (sp.xpThisSeason < def.xpRequired) return null;
  if (sp.claimedTiers.includes(tier)) return null;

  // Apply reward
  applyReward(p, def.reward);
  sp.claimedTiers.push(tier);
  // Keep claimedTiers sorted for stable UI ordering and idempotent diffs.
  sp.claimedTiers.sort((a, b) => a - b);
  try {
    saveProgress(p);
  } catch {
    // Test envs without localStorage hooked — state already mutated.
  }
  return def;
}

/**
 * Claim ALL currently-pending tiers in one shot. Useful for the "👀 一鍵
 * 領取" home button. Returns the list of tier defs actually claimed
 * (empty when nothing was pending).
 */
export function claimAllPending(p: PlayerProgress): SeasonTier[] {
  const pending = pendingClaims(p);
  const out: SeasonTier[] = [];
  for (const t of pending) {
    const def = claimTier(p, t.tier);
    if (def) out.push(def);
  }
  return out;
}

/** Apply a single reward. Pure side-effect helper, no save. */
function applyReward(p: PlayerProgress, reward: SeasonReward): void {
  switch (reward.kind) {
    case 'freeXP':
      addFreeXP(p, reward.amount);
      return;
    case 'cosmetic':
    case 'mascot':
    case 'boss': {
      // The id already carries its `kind:` prefix (e.g. 'cosmetic:season1-tank-dawn').
      // Dedup push so re-grant scenarios (handcrafted fixture) stay idempotent.
      if (!p.unlockedSkills.includes(reward.id)) {
        p.unlockedSkills.push(reward.id);
      }
      return;
    }
    default: {
      const _exhaustive: never = reward;
      void _exhaustive;
      return;
    }
  }
}

// ── Season reset / tick ─────────────────────────────────────────────────────

/**
 * Snapshot the season state at the moment the previous season ended — used
 * by the reset modal to show「上季成果」. Returned by `tickSeason` IFF a
 * reset just occurred on this tick.
 */
export interface SeasonResetInfo {
  endedSeason: number;
  xpEarned: number;
  highestTierReached: number;
  totalTiers: number;
  tiersClaimed: number[];
  /** Previous longest record BEFORE this season's contribution — for
   *  「打破紀錄!」 banners when xpEarned > prevLongest. */
  prevLongest: number;
  /** True iff `xpEarned > prevLongest` (i.e. new historical best). */
  newRecord: boolean;
}

/**
 * Idempotent season-rollover tick. Call this on home render BEFORE
 * surfacing the tier track. Behavior:
 *
 *   1. Seed `seasonStartedAt` if unseeded (mirrors dayone-hook).
 *   2. If season expired (elapsed >= length):
 *        a. Snapshot summary into SeasonResetInfo
 *        b. Bump longestSeasonXp if a new record was set
 *        c. Reset xpThisSeason = 0, claimedTiers = []
 *        d. Bump currentSeason and re-seed seasonStartedAt = nowMs
 *        e. saveProgress(p)
 *        f. Return the summary
 *   3. Otherwise return null.
 *
 * Note: we DO NOT auto-claim unclaimed tiers on reset — if the kid earned
 * tier 5 but never tapped Claim, they lose the reward. UI surfaces a
 * 「快領取!」 warning starting at day-N (handled by the banner) so this is
 * by design (loss-aversion mechanic feeding the next-season hook).
 */
export function tickSeason(p: PlayerProgress, nowMs: number = Date.now()): SeasonResetInfo | null {
  seedSeasonIfNeeded(p, nowMs);
  if (!isSeasonExpired(p, nowMs)) return null;

  const sp = getSeasonPass(p);
  // `longestSeasonXp` is bumped live by `addSeasonXP`, so by the time we hit
  // tickSeason the live longest ALREADY reflects this season's contribution.
  // To compute `newRecord` faithfully (= "did this season set a fresh
  // historical best?") we treat it as the season-just-ended being the
  // current record holder (i.e. xpEarned >= longest after positive earnings).
  //
  // Why `>=` rather than `>`: if the kid tied or beat the previous best,
  // longest will equal xpEarned exactly (live-bump rule), so any xpEarned
  // that matches longest IS the new record. We also gate on `xpEarned > 0`
  // to avoid declaring a 0-XP "season" with a 0 longest as a record.
  const newRecord = sp.xpThisSeason > 0 && sp.xpThisSeason >= sp.longestSeasonXp;
  const info: SeasonResetInfo = {
    endedSeason: sp.currentSeason,
    xpEarned: sp.xpThisSeason,
    highestTierReached: highestUnlockedTier(sp.xpThisSeason),
    totalTiers: SEASON_TIERS.length,
    tiersClaimed: [...sp.claimedTiers],
    // `prevLongest` is the longest BEFORE this season's contribution —
    // useful for the「打破紀錄!」copy. We recompute it as
    // `max(0, longest - xpEarned)` since live-bump means whatever we credited
    // this season is now baked into longest. NOTE: this is the LOWER BOUND
    // on actual prior longest (caller doesn't get a perfect history but
    // the UI doesn't need one for the celebrate copy).
    prevLongest: Math.max(0, sp.longestSeasonXp - sp.xpThisSeason),
    newRecord,
  };

  // Update longest (defensive — addSeasonXP already does this, but ensure
  // a handcrafted fixture with a stale longest gets a clean bump too).
  if (sp.xpThisSeason > sp.longestSeasonXp) sp.longestSeasonXp = sp.xpThisSeason;

  sp.currentSeason += 1;
  sp.seasonStartedAt = nowMs;
  sp.xpThisSeason = 0;
  sp.claimedTiers = [];

  try {
    saveProgress(p);
  } catch {
    // ignore — see claimTier
  }

  return info;
}

// ── Snapshot for UI ─────────────────────────────────────────────────────────

/** One row in the tier-track view-model. */
export interface TierProgress {
  def: SeasonTier;
  unlocked: boolean;
  claimed: boolean;
  /** True iff unlocked but not yet claimed (drives the 「✨ 可領取」 badge). */
  claimable: boolean;
}

export interface SeasonSnapshot {
  /** Whether the season has been seeded yet (false on a brand-new player
   *  who hasn't hit home with a tick). */
  seeded: boolean;
  seasonNumber: number;
  seasonName: string;
  seasonSubtitle: string;
  /** Logical day within the season (1..lengthDays). 0 if unseeded. */
  dayInSeason: number;
  lengthDays: number;
  /** Calendar days remaining (lengthDays - dayInSeason + 1, clamped >= 0). */
  daysRemaining: number;
  xpThisSeason: number;
  longestSeasonXp: number;
  /** Highest tier number currently unlocked (0 if none). */
  highestUnlocked: number;
  /** Next tier the kid is grinding toward; null if all done. */
  next: SeasonTier | null;
  /** XP delta still needed to reach `next` (0 if all done or already unlocked). */
  xpToNext: number;
  /** Per-tier rows in 1..N order. */
  tiers: TierProgress[];
  /** Pending-claim count for the「✨ N 個獎勵可領」 badge. */
  pendingCount: number;
  /** True iff season window has elapsed and the next tick will reset. */
  expired: boolean;
}

export function snapshotSeason(p: PlayerProgress, nowMs: number = Date.now()): SeasonSnapshot {
  const sp = getSeasonPass(p);
  const xp = sp.xpThisSeason;
  const claimed = new Set(sp.claimedTiers);
  const highest = highestUnlockedTier(xp);
  const next = nextTier(xp);
  const tiers: TierProgress[] = SEASON_TIERS.map((def) => {
    const unlocked = xp >= def.xpRequired;
    const isClaimed = claimed.has(def.tier);
    return {
      def,
      unlocked,
      claimed: isClaimed,
      claimable: unlocked && !isClaimed,
    };
  });
  const dayInSeason = currentSeasonDay(p, nowMs);
  const daysRemaining = Math.max(0, sp.seasonLengthDays - dayInSeason + (dayInSeason > 0 ? 1 : 0));
  return {
    seeded: sp.seasonStartedAt > 0,
    seasonNumber: sp.currentSeason,
    seasonName: SEASON_DEF.name,
    seasonSubtitle: SEASON_DEF.subtitle,
    dayInSeason,
    lengthDays: sp.seasonLengthDays,
    daysRemaining,
    xpThisSeason: xp,
    longestSeasonXp: sp.longestSeasonXp,
    highestUnlocked: highest,
    next,
    xpToNext: next === null ? 0 : Math.max(0, next.xpRequired - xp),
    tiers,
    pendingCount: tiers.filter((t) => t.claimable).length,
    expired: isSeasonExpired(p, nowMs),
  };
}

// ── Integration helpers (called by home.ts wire-up) ─────────────────────────

/**
 * One-shot convenience invoked by home.ts on every home render. Handles:
 *   1. Tick (lazy seed + possible auto-reset → returns reset info for modal)
 *   2. Snapshot for the tier-track UI
 *
 * Idempotent within a UTC day (the tick is a no-op when the season hasn't
 * expired). Designed to be wrapped in try/catch by the home page so a
 * season-pass hiccup never blocks the hero CTAs.
 */
export interface SeasonHomeResult {
  resetInfo: SeasonResetInfo | null;
  snapshot: SeasonSnapshot;
}

export function processSeasonOnHomeEnter(
  p: PlayerProgress,
  nowMs: number = Date.now(),
): SeasonHomeResult {
  const resetInfo = tickSeason(p, nowMs);
  return {
    resetInfo,
    snapshot: snapshotSeason(p, nowMs),
  };
}
